import { AlbumOverlayConfig, OVERLAY_SIZE_FACTORS } from '../types';
import { computeOverlayPlacement } from './overlayPlacement';
import { overlaysApi } from '../api/overlays';

/**
 * Client-side fallback for the compositor Lambda. Loads the original photo +
 * each overlay, draws them onto an off-screen canvas, and returns the result.
 *
 * Used when `Album.processingStatus === 'failed'` so users can still view and
 * download photos with overlays applied. Order/anchor/size/opacity match the
 * Konva renderer in `AlbumPhotoCanvas.tsx` so the output looks identical.
 *
 * Notes:
 * - All assets are loaded with `crossOrigin = 'anonymous'`. The S3 + overlay
 *   endpoints already serve permissive CORS for the album viewer, so this is
 *   a no-op in normal operation but lets us read pixels off the canvas.
 * - JPEG output at quality 0.92 — matches the Lambda's output quality.
 */

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
}

interface CompositeOptions {
  /** Source URL for the base photo (typically photo.originalUrl). */
  baseUrl: string;
  /** Overlay configs from the album. */
  overlayConfigs: AlbumOverlayConfig[];
  /**
   * Maximum output dimension on the long side. Defaults to the natural size of
   * the base image (matches Lambda behaviour). Pass a smaller value for view
   * paths to keep the canvas cheap.
   */
  maxDimension?: number;
}

async function buildCanvas({ baseUrl, overlayConfigs, maxDimension }: CompositeOptions): Promise<HTMLCanvasElement> {
  const baseImage = await loadImage(baseUrl);

  // Honour the maxDimension cap on the long side, preserving aspect.
  let width = baseImage.naturalWidth;
  let height = baseImage.naturalHeight;
  if (maxDimension && Math.max(width, height) > maxDimension) {
    const scale = maxDimension / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');

  // Base layer
  ctx.drawImage(baseImage, 0, 0, width, height);

  // Overlays — load in parallel to keep the critical path short
  const overlayImages = await Promise.all(
    overlayConfigs.map(async (cfg) => {
      try {
        const img = await loadImage(overlaysApi.fileUrl(cfg.overlayId));
        return { cfg, img };
      } catch {
        // A missing overlay shouldn't block compositing — just skip it.
        return null;
      }
    }),
  );

  for (const entry of overlayImages) {
    if (!entry) continue;
    const { cfg, img } = entry;
    const sizeFactor = OVERLAY_SIZE_FACTORS[cfg.size] ?? 0.18;
    const { x, y, scale } = computeOverlayPlacement(
      cfg.anchor,
      sizeFactor,
      width,
      height,
      img.naturalWidth,
      img.naturalHeight,
    );
    const drawW = img.naturalWidth * scale;
    const drawH = img.naturalHeight * scale;

    const prevAlpha = ctx.globalAlpha;
    ctx.globalAlpha = cfg.opacity ?? 1;
    ctx.drawImage(img, x, y, drawW, drawH);
    ctx.globalAlpha = prevAlpha;
  }

  return canvas;
}

/** Composite client-side and return a JPEG Blob. */
export async function compositePhotoToBlob(opts: CompositeOptions): Promise<Blob> {
  const canvas = await buildCanvas(opts);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas toBlob returned null'))),
      'image/jpeg',
      0.92,
    );
  });
}

/** Composite client-side and return an object URL suitable for `<img src>`. */
export async function compositePhotoToObjectUrl(opts: CompositeOptions): Promise<string> {
  const blob = await compositePhotoToBlob(opts);
  return URL.createObjectURL(blob);
}
